import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowsOutCardinal, DotsThree, DownloadSimple, Eye, FilePdf, Folder,
  FolderPlus, House, PencilSimple, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_TYPE_LABELS = {
  lease: "Lease",
  insurance_declaration: "Insurance Declaration",
  property_tax: "Property Tax",
  pm_statement: "PM Statement",
  mortgage_statement: "Mortgage Statement",
  mineral_statement: "Mineral / Royalty Statement",
  other: "Other Document",
};

const fmtDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const docTypeLabel = (docType) => DOC_TYPE_LABELS[docType] || "Filed Document";
const destinationLabel = (item) => item.asset_name || item.liability_name || "—";

export default function Archive() {
  const { hid, assets } = useHousehold();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("all");
  const [assetId, setAssetId] = useState(() => searchParams.get("asset") || "all");

  const [folders, setFolders] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDest, setMoveDest] = useState("root");

  const loadFolders = () => api.get("/archive/folders").then((r) => setFolders(r.data || [])).catch(() => {});

  useEffect(() => { loadFolders(); }, []);

  useEffect(() => {
    const nextAssetId = searchParams.get("asset") || "all";
    setAssetId((current) => (current === nextAssetId ? current : nextAssetId));
  }, [searchParams]);

  useEffect(() => {
    if (!hid) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    api.get("/archive/items", {
      params: {
        household_id: hid,
        ...(docType !== "all" ? { doc_type: docType } : {}),
        ...(assetId !== "all" ? { asset_id: assetId } : {}),
      },
    })
      .then((r) => {
        if (cancelled) return;
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        toast.error("Could not load archive");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [assetId, docType, hid]);

  const childFolders = useMemo(
    () => folders.filter((f) => (f.parent_id || null) === currentId),
    [currentId, folders]
  );

  const breadcrumb = useMemo(() => {
    const path = [];
    const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
    let id = currentId;
    while (id) {
      const folder = byId[id];
      if (!folder) break;
      path.unshift(folder);
      id = folder.parent_id || null;
    }
    return path;
  }, [currentId, folders]);

  const folderOptions = useMemo(() => {
    const byParent = {};
    folders.forEach((folder) => {
      (byParent[folder.parent_id || "root"] ||= []).push(folder);
    });

    const out = [];
    const walk = (parent, depth) => {
      (byParent[parent] || [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((folder) => {
          out.push({ ...folder, depth });
          walk(folder.id, depth + 1);
        });
    };

    walk("root", 0);
    return out;
  }, [folders]);

  const descendantSet = (folderId) => {
    const byParent = {};
    folders.forEach((folder) => {
      (byParent[folder.parent_id || "root"] ||= []).push(folder);
    });
    const set = new Set([folderId]);
    const stack = [folderId];
    while (stack.length) {
      const current = stack.pop();
      (byParent[current] || []).forEach((child) => {
        set.add(child.id);
        stack.push(child.id);
      });
    }
    return set;
  };

  const excluded = moveTarget?.type === "folder" ? descendantSet(moveTarget.id) : new Set();

  const openMove = (type, item) => {
    setMoveTarget({
      type,
      id: item.id,
      name: item.name || item.label || item.filename || "Document",
      parent_id: item.parent_id,
    });
    setMoveDest(type === "folder" ? (item.parent_id || "root") : (item.folder_id || "root"));
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Folder name required");
      return;
    }
    await api.post("/archive/folders", { name: newFolderName.trim(), parent_id: currentId });
    toast.success("Folder created");
    setNewFolderOpen(false);
    setNewFolderName("");
    loadFolders();
  };

  const submitRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    await api.put(`/archive/folders/${renameTarget.id}`, {
      name: renameValue.trim(),
      parent_id: renameTarget.parent_id ?? null,
    });
    toast.success("Folder renamed");
    setRenameTarget(null);
    loadFolders();
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete "${folder.name}"? Its documents and subfolders move up one level.`)) return;
    await api.delete(`/archive/folders/${folder.id}`);
    toast.success("Folder deleted");
    if (currentId === folder.id) setCurrentId(folder.parent_id || null);
    loadFolders();
  };

  const submitMove = async () => {
    const destination = moveDest === "root" ? null : moveDest;
    try {
      if (moveTarget?.type === "folder") {
        await api.put(`/archive/folders/${moveTarget.id}`, {
          parent_id: destination,
          name: moveTarget.name,
        });
      } else {
        await api.put(`/archive/files/${moveTarget.id}/move`, { folder_id: destination });
      }
      toast.success("Moved");
      setMoveTarget(null);
      loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const fetchItemBlob = async (item, extraParams = {}) => api.get(
    `/archive/items/${item.source}/${item.id}/file`,
    { params: { household_id: hid, ...extraParams }, responseType: "blob" }
  );

  const viewItem = async (item) => {
    try {
      const popup = window.open("", "_blank");
      const r = await fetchItemBlob(item);
      const url = URL.createObjectURL(r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/pdf" }));
      if (popup) popup.location = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open document");
    }
  };

  const downloadItem = async (item) => {
    try {
      const r = await fetchItemBlob(item, { download: 1 });
      const url = URL.createObjectURL(r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (item.filename || item.label || "document").replace(/\.pdf$/i, "") + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const handleAssetFilterChange = (nextAssetId) => {
    setAssetId(nextAssetId);
    const nextParams = new URLSearchParams(searchParams);
    if (nextAssetId === "all") nextParams.delete("asset");
    else nextParams.set("asset", nextAssetId);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ploy-brass-deep))]">
            Household records
          </p>
          <h1 className="mt-2 font-serif text-4xl font-medium tracking-[-0.03em] text-[hsl(var(--ploy-ink))]">
            Archive
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[hsl(var(--ploy-ink-soft))]">
            Every filed document, across bills, leases, insurance, taxes, mortgages, and mineral statements.
          </p>
        </div>
        <Button
          onClick={() => setNewFolderOpen(true)}
          className="rounded-sm bg-[hsl(var(--ploy-rust))] text-white hover:bg-[hsl(var(--ploy-rust))]/90"
        >
          <FolderPlus size={16} weight="bold" />
          New Folder
        </Button>
      </div>

      <div className="grid gap-8 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <div className="rounded-2xl border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-panel))] p-5">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ploy-brass-deep))]">
              Legacy bill folders
            </p>
            <div className="mt-4 flex items-center gap-1 text-sm flex-wrap">
              <button
                onClick={() => setCurrentId(null)}
                className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 ${
                  !currentId ? "font-semibold text-[hsl(var(--ploy-ink))]" : "text-[hsl(var(--ploy-ink-soft))]"
                }`}
              >
                <House size={14} weight="bold" /> Root
              </button>
              {breadcrumb.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => setCurrentId(folder.id)}
                  className={`rounded-sm px-2 py-1 ${
                    currentId === folder.id ? "font-semibold text-[hsl(var(--ploy-ink))]" : "text-[hsl(var(--ploy-ink-soft))]"
                  }`}
                >
                  / {folder.name}
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {childFolders.length === 0 ? (
                <p className="rounded-xl border border-dashed border-[hsl(var(--ploy-border))] px-4 py-6 text-sm text-[hsl(var(--ploy-ink-soft))]">
                  No folders here yet. Bills can still be moved from the list on the right.
                </p>
              ) : (
                childFolders.map((folder) => (
                  <div
                    key={folder.id}
                    className="flex items-center gap-3 rounded-xl border border-[hsl(var(--ploy-border))] bg-white/70 px-3 py-3"
                  >
                    <button
                      onClick={() => setCurrentId(folder.id)}
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                      <Folder size={20} weight="fill" className="text-[hsl(var(--ploy-rust))]" />
                      <span className="truncate font-medium text-[hsl(var(--ploy-ink))]">{folder.name}</span>
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="rounded-sm p-1 text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-ink))]">
                        <DotsThree size={18} weight="bold" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-sm">
                        <DropdownMenuItem onClick={() => { setRenameTarget(folder); setRenameValue(folder.name); }}>
                          <PencilSimple size={15} className="mr-2" /> Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMove("folder", folder)}>
                          <ArrowsOutCardinal size={15} className="mr-2" /> Move
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deleteFolder(folder)} className="text-destructive">
                          <Trash size={15} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        <section className="space-y-4">
          <div className="rounded-2xl border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-card))] p-5 shadow-[0_1px_2px_hsl(var(--ploy-ink)/0.04)]">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label
                  htmlFor="archive-filter-type"
                  className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ploy-brass-deep))]"
                >
                  Document type
                </Label>
                <select
                  id="archive-filter-type"
                  data-testid="archive-filter-type"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="mt-2 flex h-11 w-full rounded-md border border-[hsl(var(--ploy-border))] bg-white px-3 text-sm text-[hsl(var(--ploy-ink))] shadow-sm"
                >
                  <option value="all">All document types</option>
                  {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label
                  htmlFor="archive-filter-asset"
                  className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ploy-brass-deep))]"
                >
                  Destination
                </Label>
                <select
                  id="archive-filter-asset"
                  data-testid="archive-filter-asset"
                  value={assetId}
                  onChange={(e) => handleAssetFilterChange(e.target.value)}
                  className="mt-2 flex h-11 w-full rounded-md border border-[hsl(var(--ploy-border))] bg-white px-3 text-sm text-[hsl(var(--ploy-ink))] shadow-sm"
                >
                  <option value="all">All assets</option>
                  {assets.map((asset) => (
                    <option key={asset.id} value={asset.id}>{asset.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {total > items.length && (
              <p
                data-testid="archive-showing-note"
                className="mt-4 text-sm text-[hsl(var(--ploy-ink-soft))]"
              >
                Showing {items.length} of {total} filed documents
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-card))] shadow-[0_1px_2px_hsl(var(--ploy-ink)/0.04)]">
            {loading ? (
              <div className="px-6 py-10 text-sm text-[hsl(var(--ploy-ink-soft))]">Loading archive…</div>
            ) : items.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--ploy-panel))]">
                  <FilePdf size={26} className="text-[hsl(var(--ploy-rust))]" />
                </div>
                <h2 className="mt-5 font-serif text-2xl text-[hsl(var(--ploy-ink))]">No filed documents</h2>
                <p className="mt-2 text-sm text-[hsl(var(--ploy-ink-soft))]">
                  Adjust the filters or file a document from Ingest to populate this archive.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[hsl(var(--ploy-border))]">
                {items.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`archive-item-${item.id}`}
                    className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between"
                  >
                    <div className="flex min-w-0 items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--ploy-panel))]">
                        <FilePdf size={20} weight="fill" className="text-[hsl(var(--ploy-rust))]" />
                      </div>
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-[15px] font-semibold text-[hsl(var(--ploy-ink))]">
                            {item.label || item.filename || "Document"}
                          </p>
                          <span className="rounded-full bg-[hsl(var(--ploy-panel))] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--ploy-rust))]">
                            {docTypeLabel(item.doc_type)}
                          </span>
                          {item.source === "bill" && (
                            <span className="rounded-full bg-[hsl(var(--ploy-rust-soft))] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--ploy-rust))]">
                              Bill
                            </span>
                          )}
                        </div>
                        <div className="grid gap-3 text-sm text-[hsl(var(--ploy-ink-soft))] md:grid-cols-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--ploy-brass-deep))]">Destination</p>
                            <p className="mt-1 font-medium text-[hsl(var(--ploy-ink))]">
                              {item.asset_id ? (
                                <Link
                                  to={`/assets/${item.asset_id}`}
                                  data-testid={`archive-dest-link-${item.id}`}
                                  className="text-[hsl(var(--ploy-rust))] hover:underline"
                                >
                                  {destinationLabel(item)}
                                </Link>
                              ) : item.liability_id ? (
                                <Link
                                  to="/liabilities"
                                  data-testid={`archive-dest-link-${item.id}`}
                                  className="text-[hsl(var(--ploy-rust))] hover:underline"
                                >
                                  {destinationLabel(item)}
                                </Link>
                              ) : (
                                destinationLabel(item)
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--ploy-brass-deep))]">Filed</p>
                            <p className="mt-1 font-medium text-[hsl(var(--ploy-ink))]">{fmtDate(item.filed_at)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--ploy-brass-deep))]">Source</p>
                            <p className="mt-1 font-medium capitalize text-[hsl(var(--ploy-ink))]">{item.source || "document"}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end lg:self-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`archive-view-${item.id}`}
                        onClick={() => viewItem(item)}
                        className="rounded-sm"
                      >
                        <Eye size={15} />
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadItem(item)}
                        className="rounded-sm text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-ink))]"
                      >
                        <DownloadSimple size={15} />
                        Download
                      </Button>
                      {item.source === "bill" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`archive-move-${item.id}`}
                          onClick={() => openMove("file", item)}
                          className="rounded-sm text-[hsl(var(--ploy-ink-soft))] hover:text-[hsl(var(--ploy-ink))]"
                        >
                          <ArrowsOutCardinal size={15} />
                          Move to folder
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-medium">New Folder</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Folder Name</Label>
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createFolder()}
              placeholder="e.g. Insurance"
              className="mt-1.5 rounded-sm"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button onClick={createFolder} className="w-full rounded-sm font-semibold">Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-medium">Rename Folder</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitRename()}
            className="rounded-sm"
            autoFocus
          />
          <DialogFooter>
            <Button onClick={submitRename} className="w-full rounded-sm font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!moveTarget} onOpenChange={(open) => !open && setMoveTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-medium">Move "{moveTarget?.name}"</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Choose a destination folder.</p>
          <div className="max-h-72 overflow-y-auto rounded-sm border border-border divide-y divide-border">
            <button
              onClick={() => setMoveDest("root")}
              className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-secondary ${
                moveDest === "root" ? "bg-secondary font-bold" : ""
              }`}
            >
              <House size={15} weight="bold" /> Archive (root)
            </button>
            {folderOptions.filter((folder) => !excluded.has(folder.id)).map((folder) => (
              <button
                key={folder.id}
                onClick={() => setMoveDest(folder.id)}
                style={{ paddingLeft: 12 + folder.depth * 18 }}
                className={`flex w-full items-center gap-2 pr-3 py-2.5 text-left text-sm hover:bg-secondary ${
                  moveDest === folder.id ? "bg-secondary font-bold" : ""
                }`}
              >
                <Folder size={15} weight="fill" /> {folder.name}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={submitMove} className="w-full rounded-sm font-semibold">Move Here</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
